/* ============================================================
   Blue Salon — About page behaviours
   Ported from the supplied design (v2).

   Differences from the source script, and why:
   1. The nav scroll handler is gone — the design's own <nav> was dropped
      in favour of the theme header.
   2. The form's submit handler is gone. The source called
      preventDefault() and faked a success note; the form now posts
      through Shopify so the confirmation is rendered server-side.
   3. Elements are found by data-attribute within the section rather than
      by document ID, and init re-runs on shopify:section:load so the
      theme editor does not leave dead handlers behind.
   ============================================================ */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  function setup(root) {
    if (root.dataset.bsaReady === 'true') return;
    root.dataset.bsaReady = 'true';

    var prefersReducedMotion = reduce.matches;

    /* ---------- header offset ----------
       Everything above .bsa-page (announcement bar + header) is whatever the
       theme renders, and its height changes with breakpoint. Measuring the
       page's own document offset avoids hard-coding a header selector or a
       magic number: it is exactly the space the hero has to give back so it
       fills the visible area instead of overshooting the fold. */
    function setHeaderOffset() {
      var top = Math.round(root.getBoundingClientRect().top + window.scrollY);
      if (top < 0) top = 0;
      root.style.setProperty('--bsa-header-h', top + 'px');
    }
    setHeaderOffset();

    /* ---------- scroll progress bar ---------- */
    var bar = root.querySelector('[data-scroll-progress]');
    function updateScrollProgress() {
      if (!bar) return;
      var doc = document.documentElement;
      var scrollTop = doc.scrollTop || document.body.scrollTop;
      var scrollHeight = doc.scrollHeight - doc.clientHeight;
      var pct = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
      bar.style.width = pct + '%';
    }

    /* ---------- hero parallax ---------- */
    var heroInner = root.querySelector('[data-hero-inner]');
    var heroSection = root.querySelector('[data-hero]');
    function updateHeroParallax() {
      if (prefersReducedMotion || !heroInner || !heroSection) return;
      var y = window.scrollY;
      var heroHeight = heroSection.offsetHeight;
      if (y < heroHeight) {
        heroInner.style.transform = 'translateY(' + y * 0.28 + 'px)';
        heroInner.style.opacity = Math.max(1 - y / (heroHeight * 0.8), 0);
      }
    }

    /* ---------- timeline rail ----------
       Six eras laid across rather than down the page, one slide in view at a
       time. The scrolling is the browser's own, with snap points; this only
       adds the arrows, the edge fades and a measured step - and the arrows
       ship hidden, so without script they are not two dead controls. */
    var track = root.querySelector('[data-timeline-track]');
    var timelineNav = root.querySelector('[data-timeline-nav]');
    var timelinePrev = root.querySelector('[data-timeline-prev]');
    var timelineNext = root.querySelector('[data-timeline-next]');
    var timelineTrack = root.querySelector('[data-timeline-dots]');
    var timelineDots = root.querySelectorAll('[data-timeline-go]');

    /* One era's worth of scrolling. Measured off the slot, not the panel
       inside it: the panel can be inset within its slot, and stepping by its
       width under-scrolls and lets the snap throw the rail several eras on. */
    function timelineStep() {
      if (!track) return 0;
      var slot = track.querySelector('.tl-slot');
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return slot ? slot.getBoundingClientRect().width + gap : track.clientWidth;
    }

    /* Which era is in view. Everything that moves the rail works in these
       whole eras rather than in pixels: the last panel comes to rest flush
       with the rail's end, which is short of its own full step, so a step
       measured in pixels lands between eras once you are near the end. */
    function currentEra() {
      if (!track || !timelineDots.length) return 0;
      var last = timelineDots.length - 1;
      var step = timelineStep();
      var travel = track.scrollWidth - track.clientWidth;
      if (travel > 0 && track.scrollLeft >= travel - 1) return last;
      var index = step ? Math.round(track.scrollLeft / step) : 0;
      if (index < 0) return 0;
      if (index > last) return last;
      return index;
    }

    /* Which era is lit on the line beneath the rail. */
    function markEra() {
      if (!track || !timelineDots.length) return;
      var last = timelineDots.length - 1;
      var index = currentEra();

      for (var d = 0; d <= last; d++) {
        if (d === index) timelineDots[d].setAttribute('aria-current', 'true');
        else timelineDots[d].removeAttribute('aria-current');
      }
      if (timelineTrack) {
        timelineTrack.style.setProperty('--tl-progress', last ? (index / last).toFixed(4) : '0');
      }
    }

    function updateTimeline() {
      if (!track) return;
      var travel = track.scrollWidth - track.clientWidth;
      var atStart = track.scrollLeft <= 1;
      var atEnd = travel <= 0 || track.scrollLeft >= travel - 1;

      if (timelinePrev) timelinePrev.disabled = atStart;
      if (timelineNext) timelineNext.disabled = atEnd;

      /* Drives the edge fades: each side shows only while there is something
         that way to scroll to. */
      var wrap = track.parentElement;
      if (wrap) {
        wrap.classList.toggle('has-prev', !atStart);
        wrap.classList.toggle('has-next', !atEnd);
      }

      markEra();
    }

    if (track) {
      track.addEventListener('scroll', function () {
        window.requestAnimationFrame(updateTimeline);
      }, { passive: true });

      if (timelineNav) {
        timelineNav.hidden = false;
        timelineNav.removeAttribute('aria-hidden');
      }

      var sliding = false;
      var SLIDE_MS = 620;

      /* Tweened by hand rather than with `behavior: 'smooth'`, which varies
         by browser and at this distance either flicks across or drags.

         Snapping has to be off while the tween runs or the scroller fights it
         for the scroll position; it goes back on at the end, landing exactly
         on a snap point. */
      var slideTo = function (target) {
        var start = track.scrollLeft;
        var delta = target - start;
        if (!delta) return;

        if (prefersReducedMotion) {
          track.scrollLeft = target;
          return;
        }

        sliding = true;
        track.style.scrollSnapType = 'none';
        var began = performance.now();

        var frame = function (now) {
          var p = Math.min((now - began) / SLIDE_MS, 1);
          /* Leaves quickly and comes to rest rather than stopping dead. */
          var eased = 1 - Math.pow(1 - p, 3);
          track.scrollLeft = start + delta * eased;
          if (p < 1) {
            window.requestAnimationFrame(frame);
          } else {
            track.scrollLeft = target;
            track.style.scrollSnapType = '';
            sliding = false;
          }
        };
        window.requestAnimationFrame(frame);
      };

      /* Straight to a decade, rather than stepping through the ones before
         it. The dots are real controls and stay in the tab order - unlike the
         arrows, they are the only way to make this jump. */
      var goToEra = function (index) {
        if (sliding) return;
        var last = timelineDots.length ? timelineDots.length - 1 : 0;
        if (index < 0) index = 0;
        if (index > last) index = last;
        var travel = track.scrollWidth - track.clientWidth;
        slideTo(Math.min(index * timelineStep(), travel));
      };

      /* One era per press, counted rather than measured - see currentEra. */
      var stepTimeline = function (direction) {
        /* A second press mid-slide would restart the tween from wherever the
           rail had got to and land between eras. */
        if (sliding) return;
        if (timelineDots.length) {
          goToEra(currentEra() + direction);
          return;
        }
        var travel = track.scrollWidth - track.clientWidth;
        slideTo(Math.min(Math.max(track.scrollLeft + direction * timelineStep(), 0), travel));
      };
      for (var g = 0; g < timelineDots.length; g++) {
        (function (i) {
          timelineDots[i].addEventListener('click', function () { goToEra(i); });
        })(g);
      }
      if (timelinePrev) {
        timelinePrev.removeAttribute('tabindex');
        timelinePrev.setAttribute('aria-label', 'Previous era');
        timelinePrev.addEventListener('click', function () { stepTimeline(-1); });
      }
      if (timelineNext) {
        timelineNext.removeAttribute('tabindex');
        timelineNext.setAttribute('aria-label', 'Next era');
        timelineNext.addEventListener('click', function () { stepTimeline(1); });
      }
    }

    /* ---------- events window ----------
       Six events on screen and one moves at a time: the first leaves, every
       other card slides back a place, and the next unseen event arrives in
       the last slot.

       A scroller cannot do that - a card at the head of a row has to travel
       up and across to the end of the row above - so the order is changed
       and each card animated from where it was to where it now is. Positions
       are read before and after the change and the difference is played
       back as a transform, which keeps the browser doing the layout and this
       code doing arithmetic. */
    var eventsRail = root.querySelector('[data-events-track]');
    if (eventsRail) {
      var eventCards = [].slice.call(eventsRail.querySelectorAll('.event-card'));
      var eventsNav = root.querySelector('[data-events-nav]');
      var eventsDotList = root.querySelector('[data-events-dots]');
      var eventsPrev = root.querySelector('[data-events-prev]');
      var eventsNext = root.querySelector('[data-events-next]');
      var eventsOffset = 0;
      var eventsBusy = false;
      var eventsDots = [];
      var EVENTS_ROWS = 2;
      var EVENTS_MS = 520;

      /* How many fit on screen, read off the grid rather than assumed: three
         columns on a wide screen, two on a tablet, one on a phone. */
      function eventsWindow() {
        var cols = getComputedStyle(eventsRail).gridTemplateColumns.split(' ').length;
        if (!cols || cols < 1) cols = 1;
        return cols * EVENTS_ROWS;
      }
      function eventsStops() {
        return Math.max(1, eventCards.length - eventsWindow() + 1);
      }

      /* Lay the window out at this offset. With `animate` the cards that stay
         are slid from their old places and the one that arrives is faded in. */
      function showEvents(offset, animate) {
        var size = eventsWindow();
        var last = eventsStops() - 1;
        if (offset < 0) offset = 0;
        if (offset > last) offset = last;

        var before = {};
        if (animate) {
          for (var b = 0; b < eventCards.length; b++) {
            if (!eventCards[b].hidden) before[b] = eventCards[b].getBoundingClientRect();
          }
        }

        eventsOffset = offset;
        for (var i = 0; i < eventCards.length; i++) {
          var slot = i - offset;
          var inWindow = slot >= 0 && slot < size;
          eventCards[i].hidden = !inWindow;
          eventCards[i].style.order = inWindow ? slot : '';
        }

        if (animate) {
          for (var j = 0; j < eventCards.length; j++) {
            var card = eventCards[j];
            if (card.hidden) continue;
            var from = before[j];
            var to = card.getBoundingClientRect();
            card.style.transition = 'none';
            if (from) {
              var dx = Math.round(from.left - to.left);
              var dy = Math.round(from.top - to.top);
              if (!dx && !dy) continue;
              card.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
            } else {
              /* the newcomer has no old place to come from */
              card.style.opacity = '0';
            }
            /* read once so the browser commits the start of the move before
               the transition is switched on */
            void card.offsetWidth;
            card.style.transition = 'transform ' + EVENTS_MS + 'ms ease, opacity ' + EVENTS_MS + 'ms ease';
            card.style.transform = '';
            card.style.opacity = '';
          }
          eventsBusy = true;
          window.setTimeout(function () {
            for (var k = 0; k < eventCards.length; k++) {
              eventCards[k].style.transition = '';
            }
            eventsBusy = false;
          }, EVENTS_MS + 40);
        }

        updateEventsNav();
      }

      function updateEventsNav() {
        var last = eventsStops() - 1;
        if (eventsPrev) eventsPrev.disabled = eventsOffset <= 0;
        if (eventsNext) eventsNext.disabled = eventsOffset >= last;
        for (var d = 0; d < eventsDots.length; d++) {
          if (d === eventsOffset) eventsDots[d].setAttribute('aria-current', 'true');
          else eventsDots[d].removeAttribute('aria-current');
        }
      }

      /* A dot per position, while they stay countable - twenty events is
         fifteen positions, and that many dots is a smear rather than a place.
         Past ten the arrows carry it alone. */
      function buildEventsDots() {
        if (!eventsDotList) return;
        var stops = eventsStops();
        eventsDots = [];
        eventsDotList.innerHTML = '';
        if (stops < 2 || stops > 10) return;
        for (var i = 0; i < stops; i++) {
          (function (n) {
            var li = document.createElement('li');
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'events-dot';
            b.setAttribute('aria-label', 'Event ' + (n + 1) + ' of ' + stops);
            b.addEventListener('click', function () {
              if (eventsBusy) return;
              eventsStop();
              showEvents(n, !prefersReducedMotion);
            });
            li.appendChild(b);
            eventsDotList.appendChild(li);
            eventsDots.push(b);
          })(i);
        }
      }

      var stepEvents = function (direction) {
        if (eventsBusy) return;
        eventsStop();
        showEvents(eventsOffset + direction, !prefersReducedMotion);
      };

      if (eventsNav) {
        eventsNav.hidden = false;
        eventsNav.removeAttribute('aria-hidden');
      }
      if (eventsPrev) {
        eventsPrev.removeAttribute('tabindex');
        eventsPrev.addEventListener('click', function () { stepEvents(-1); });
      }
      if (eventsNext) {
        eventsNext.removeAttribute('tabindex');
        eventsNext.addEventListener('click', function () { stepEvents(1); });
      }

      var eventsResize;
      window.addEventListener('resize', function () {
        window.clearTimeout(eventsResize);
        eventsResize = window.setTimeout(function () {
          buildEventsDots();
          showEvents(eventsOffset, false);
        }, 180);
      });

      /* ---- moving on its own ----
         One event every few seconds, so the board shows what it holds without
         being asked. It only runs while the section is actually on screen and
         nobody is reading it: off-screen it would be animating to nobody, and
         under the cursor it would move the card out from under a click.

         Reaching the end it returns to the first, without animating - the
         shift from the last window to the first is every card at once, which
         is a shuffle rather than a step.

         Anyone who has asked for less movement gets a board that waits to be
         told, and so does anyone who takes hold of the controls: once the
         reader has chosen a direction, the section should stop choosing. */
      var eventsTimer = null;
      var eventsStopped = prefersReducedMotion;
      var eventsOnScreen = false;
      var eventsHovered = false;
      var EVENTS_EVERY = 5000;

      function eventsPlaying() {
        return !eventsStopped && eventsOnScreen && !eventsHovered && eventsStops() > 1;
      }
      function eventsTick() {
        if (!eventsPlaying()) return;
        var last = eventsStops() - 1;
        if (eventsOffset >= last) showEvents(0, false);
        else showEvents(eventsOffset + 1, true);
      }
      function eventsAuto() {
        window.clearInterval(eventsTimer);
        eventsTimer = null;
        if (!eventsPlaying()) return;
        eventsTimer = window.setInterval(eventsTick, EVENTS_EVERY);
      }
      function eventsStop() {
        eventsStopped = true;
        window.clearInterval(eventsTimer);
        eventsTimer = null;
      }

      var eventsSection = eventsRail.closest('.events-section') || eventsRail;
      eventsSection.addEventListener('mouseenter', function () { eventsHovered = true; eventsAuto(); });
      eventsSection.addEventListener('mouseleave', function () { eventsHovered = false; eventsAuto(); });
      eventsSection.addEventListener('focusin', function () { eventsHovered = true; eventsAuto(); });
      eventsSection.addEventListener('focusout', function () { eventsHovered = false; eventsAuto(); });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          eventsOnScreen = entries[0].isIntersecting;
          eventsAuto();
        }, { threshold: 0.2 }).observe(eventsSection);
      } else {
        eventsOnScreen = true;
      }

      buildEventsDots();
      showEvents(0, false);
      eventsAuto();
    }

    /* one scroll listener drives all three, coalesced into a frame */
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        updateScrollProgress();
        updateHeroParallax();
        updateTimeline();
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      setHeaderOffset();
      onScroll();
    });
    updateScrollProgress();
    updateHeroParallax();
    updateTimeline();

    /* webfonts land after first paint and change the header's height */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(setHeaderOffset);
    }

    /* ---------- count-up stat numbers ----------
       A figure with data-compact is held as its real number and printed short
       - 240K, 1M. Customers used to be carried as a target of 1 with an "M+"
       suffix, which meant Math.floor sat on zero for the whole animation and
       then snapped to 1: it never counted. Held as 1000000 it runs up through
       the hundred-thousands like the others and lands on the same "1M+". */
    var statNums = root.querySelectorAll('.stat-num[data-target]');

    var compactFormatter = null;
    if (window.Intl && Intl.NumberFormat) {
      try {
        compactFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          notation: 'compact',
          maximumFractionDigits: 0
        });
      } catch (e) {
        /* notation: 'compact' is not universal; fall through to the plain number */
        compactFormatter = null;
      }
    }

    function renderStat(el, value) {
      var suffix = el.dataset.suffix || '';
      if (el.hasAttribute('data-compact') && compactFormatter) {
        return compactFormatter.format(value) + suffix;
      }
      return value + suffix;
    }

    function animateCount(el) {
      var target = parseFloat(el.dataset.target);
      if (prefersReducedMotion) {
        el.textContent = renderStat(el, target);
        return;
      }
      var duration = 1500;
      var startTime = performance.now();
      el.dataset.counting = 'true';
      function tick(now) {
        var p = Math.min((now - startTime) / duration, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = renderStat(el, Math.floor(eased * target));
        if (p < 1) window.requestAnimationFrame(tick);
        else el.textContent = renderStat(el, target);
      }
      window.requestAnimationFrame(tick);
    }
    if ('IntersectionObserver' in window && statNums.length) {
      /* Re-counts on each pass, matching the reveals. The guard stops a
         half-finished count restarting if the element flickers across the
         threshold mid-animation. */
      var statIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            if (entry.target.dataset.counting !== 'true') animateCount(entry.target);
          } else {
            entry.target.dataset.counting = 'false';
          }
        });
      }, { threshold: 0.5 });
      for (var s = 0; s < statNums.length; s++) statIO.observe(statNums[s]);
    } else {
      for (var s2 = 0; s2 < statNums.length; s2++) {
        statNums[s2].textContent = renderStat(statNums[s2], parseFloat(statNums[s2].dataset.target));
      }
    }

    /* ---------- marquee: pause on hover ---------- */
    var rows = root.querySelectorAll('.marquee-row');
    for (var r = 0; r < rows.length; r++) {
      (function (row) {
        row.addEventListener('mouseenter', function () { row.classList.add('paused'); });
        row.addEventListener('mouseleave', function () { row.classList.remove('paused'); });
      })(rows[r]);
    }

    /* ---------- magnetic tilt on brand plates ---------- */
    if (!prefersReducedMotion) {
      var plates = root.querySelectorAll('.plate');
      for (var p2 = 0; p2 < plates.length; p2++) {
        (function (plate) {
          plate.addEventListener('mousemove', function (e) {
            var rect = plate.getBoundingClientRect();
            var x = e.clientX - rect.left;
            var y = e.clientY - rect.top;
            var cx = rect.width / 2;
            var cy = rect.height / 2;
            var rotateY = ((x - cx) / cx) * 9;
            var rotateX = -((y - cy) / cy) * 9;
            plate.style.transform =
              'perspective(600px) rotateX(' + rotateX + 'deg) rotateY(' + rotateY + 'deg) translateY(-3px)';
          });
          plate.addEventListener('mouseleave', function () {
            plate.style.transform = '';
          });
        })(plates[p2]);
      }
    }

    /* ---------- story video ----------
       Autoplays when it scrolls into view rather than on page load, so the
       film is not streamed to someone who never reaches it.

       It is MUTED because every browser blocks autoplay with sound — that is a
       platform rule, not a preference. Controls are on so a visitor can unmute.
       Under prefers-reduced-motion the poster simply stays. */
    var mediaBox = root.querySelector('[data-story-video]');
    if (mediaBox && !prefersReducedMotion) {
      var buildPlayer = function () {
        var url = mediaBox.getAttribute('data-story-video') || '';
        var title = mediaBox.getAttribute('data-story-video-title') || 'Video';

        var yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
        var vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);

        var player = document.createElement('div');
        player.className = 'story-player';

        if (yt) {
          var f = document.createElement('iframe');
          f.src =
            'https://www.youtube-nocookie.com/embed/' + yt[1] +
            '?autoplay=1&mute=1&playsinline=1&rel=0&modestbranding=1';
          f.title = title;
          f.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
          f.setAttribute('allowfullscreen', '');
          player.appendChild(f);
        } else if (vimeo) {
          var fv = document.createElement('iframe');
          fv.src = 'https://player.vimeo.com/video/' + vimeo[1] + '?autoplay=1&muted=1&playsinline=1';
          fv.title = title;
          fv.allow = 'autoplay; fullscreen; picture-in-picture';
          fv.setAttribute('allowfullscreen', '');
          player.appendChild(fv);
        } else {
          var v = document.createElement('video');
          v.src = url;
          v.controls = true;
          v.autoplay = true;
          v.muted = true;
          v.setAttribute('muted', '');
          v.playsInline = true;
          v.setAttribute('playsinline', '');
          v.preload = 'metadata';
          player.appendChild(v);
        }

        mediaBox.replaceWith(player);
      };

      if ('IntersectionObserver' in window) {
        var vIO = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              vIO.unobserve(e.target);
              buildPlayer();
            }
          });
        }, { threshold: 0.35 });
        vIO.observe(mediaBox);
      } else {
        buildPlayer();
      }
    }

    /* ---------- "request our profile" ----------
       The profile is emailed by the Business Development team rather than
       served as a file, so the button hands the visitor to the enquiry form
       instead of downloading anything: it reveals the note explaining what
       will happen, seeds the message if they have not written one, tags the
       enquiry so the team can see what it is for, then scrolls the form into
       view and puts the cursor in the first field.

       Focus is deferred past the smooth scroll - focusing first would make the
       browser jump to the field and cancel the animation. */
    var profileBtn = root.querySelector('[data-bsa-profile-request]');
    var partnerForm = document.getElementById('bsa-partner-form');
    if (profileBtn && partnerForm) {
      profileBtn.addEventListener('click', function () {
        var note = partnerForm.querySelector('[data-bsa-profile-note]');
        var flag = partnerForm.querySelector('[data-bsa-profile-flag]');
        var message = partnerForm.querySelector('#bsa-message');
        var first = partnerForm.querySelector('#bsa-fname');

        if (flag) {
          flag.value = profileBtn.getAttribute('data-bsa-request-value') || 'Company profile requested';
        }
        if (message && !message.value.trim()) {
          message.value = profileBtn.getAttribute('data-bsa-message') || '';
        }
        if (note) {
          note.hidden = false;
        }

        partnerForm.scrollIntoView({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          block: 'center'
        });

        if (first) {
          window.setTimeout(function () {
            first.focus({ preventScroll: true });
          }, prefersReducedMotion ? 0 : 450);
        }
      });
    }

    /* ---------- brand archive: marquee <-> grid ----------
       The two are alternate views of one list, so opening the grid closes the
       ribbon. The stack's height is measured and animated rather than simply
       hidden, because dropping ~200px out of the layout in one frame throws the
       page under the reader's cursor. */
    var archive = root.querySelector('[data-brand-archive]');
    if (archive) {
      var toggle = archive.querySelector('[data-brand-toggle]');
      var grid = archive.querySelector('[data-brand-grid]');
      var stack = archive.querySelector('[data-marquee-stack]');
      var toggleLabel = archive.querySelector('[data-brand-toggle-label]');

      if (toggle && grid && stack) {
        /* the control only works with script, so it is enabled from script */
        toggle.hidden = false;

        /* Cap the stagger so a long list does not finish on a long delay: the
           index resets every 12 cells, which keeps the sweep readable without
           the last name waiting half a second. */
        var cells = grid.querySelectorAll('.brand-cell');
        for (var c = 0; c < cells.length; c++) {
          cells[c].style.setProperty('--i', c % 12);
        }

        var open = false;

        /* Pad the last row so the lattice always closes on a full rectangle.
           Column count comes from the computed grid rather than a breakpoint
           table, so it stays right whatever the container width resolves to —
           but it only reads correctly while the grid is displayed. */
        function padGrid() {
          if (grid.hidden) return;
          var fillers = grid.querySelectorAll('.brand-cell.is-filler');
          for (var f = 0; f < fillers.length; f++) fillers[f].remove();

          var cols = window.getComputedStyle(grid).gridTemplateColumns.split(' ').length;
          if (!cols || cols < 1) return;
          var real = grid.querySelectorAll('.brand-cell').length;
          var need = (cols - (real % cols)) % cols;
          for (var n = 0; n < need; n++) {
            var pad = document.createElement('div');
            pad.className = 'brand-cell is-filler';
            pad.setAttribute('aria-hidden', 'true');
            grid.appendChild(pad);
          }
        }
        window.addEventListener('resize', padGrid);

        function setStackHeight(px) {
          stack.style.maxHeight = px === null ? '' : px + 'px';
        }

        toggle.addEventListener('click', function () {
          open = !open;
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (toggleLabel) {
            toggleLabel.textContent = open
              ? toggle.getAttribute('data-label-close')
              : toggle.getAttribute('data-label-open');
          }

          if (open) {
            /* fix the current height, then collapse from it */
            setStackHeight(stack.scrollHeight);
            grid.hidden = false;
            /* Flush layout synchronously so both transitions have a start value
               to move away from. requestAnimationFrame would read better, but
               frames are throttled in a background tab — the callback can then
               land after a subsequent click and leave the state inverted. A
               forced reflow cannot get out of order. */
            padGrid();
            void grid.offsetHeight;
            setStackHeight(0);
            archive.classList.add('is-open');
          } else {
            archive.classList.remove('is-open');
            /* grow back to the measured height; the cap is only released once
               the transition has finished, since clearing it early would snap
               straight to full height and cancel the animation */
            void stack.offsetHeight;
            setStackHeight(stack.scrollHeight);
            window.setTimeout(function () {
              if (!open) setStackHeight(null);
            }, prefersReducedMotion ? 0 : 600);
            /* hide the grid only once its cells have faded, so it does not
               disappear mid-transition */
            window.setTimeout(function () {
              if (!open) grid.hidden = true;
            }, prefersReducedMotion ? 0 : 420);
          }
        });
      }
    }

    /* ---------- reveal on scroll ----------
       Reveals repeat. This previously called io.unobserve() on first
       intersection, so every element animated exactly once and stayed put —
       which is why a second pass down the page was static.

       Direction matters too: an element should rise from below when you scroll
       down onto it, and drop from above when you scroll back up onto it.
       entry.boundingClientRect.top tells us which edge it left by, so the
       element is pre-set to re-enter from that same side. No scroll listener
       and no direction guessing needed. */
    var revealEls = root.querySelectorAll('.reveal');
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var el = entry.target;
          if (entry.isIntersecting) {
            el.classList.add('in-view');
          } else {
            var rect = entry.boundingClientRect;
            var viewportH = window.innerHeight || document.documentElement.clientHeight;
            /* Only un-reveal on a vertical exit. An element clipped out of a
               horizontal rail — a timeline card scrolled aside — has not left
               the viewport at all; it is still level with the reader. Fading
               it out there blanks something they have already read, and the
               fade back in fights the rail's own movement. */
            if (rect.bottom > 0 && rect.top < viewportH) return;
            el.classList.remove('in-view');
            /* left past the top edge → it will come back from above */
            if (rect.top < 0) el.classList.add('from-above');
            else el.classList.remove('from-above');
          }
        });
      }, { threshold: 0.12 });
      for (var i = 0; i < revealEls.length; i++) io.observe(revealEls[i]);
    } else {
      /* no observer: show everything rather than leaving the page blank */
      for (var j = 0; j < revealEls.length; j++) revealEls[j].classList.add('in-view');
    }
  }

  function init() {
    var pages = document.querySelectorAll('.bsa-page');
    for (var i = 0; i < pages.length; i++) setup(pages[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* the theme editor re-renders section markup on every settings change */
  document.addEventListener('shopify:section:load', init);
})();
